// IL SEME, SUL WEB — identità che rinasce dalle sole 12 frasi, dentro il browser.
//
// È lo stesso rito dell'app (`/opt/apkbuild/src/lib/seme.js`), parola per parola:
//   chiave = ml_dsa65.keygen( argon2id( sha256("lattice-caveau-v1:"+frasi),
//                                       "lattice-seme-chiave-v1:"+indirizzo,
//                                       t=2, m=19456 KiB, p=1, 32 byte ) )
// Stesse frasi + stesso indirizzo = stessa chiave ML-DSA-65, sul telefono e qui.
// L'indirizzo è DENTRO il sale: le stesse parole su due indirizzi danno chiavi diverse.
//
// Niente di tutto questo lascia il browser: al nodo arriva una firma, mai il segreto.
import { ml_dsa65 } from "@noble/post-quantum/ml-dsa.js";
import { argon2id } from "@noble/hashes/argon2.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "./crypto";
import { PAROLE } from "./parole12";

const ARGON = { t: 2, m: 19456, p: 1, dkLen: 32 };
const utf8 = (s) => new TextEncoder().encode(s);

/// Spazi doppi, maiuscole, invii a capo: la gente scrive come capita, e va bene.
export const normalizza = (testo) =>
  String(testo || "").toLowerCase().replace(/[^a-z ]+/g, " ").trim().replace(/\s+/g, " ");

/// I 32 byte da cui nasce tutto: dalle parole, non dalla chiave.
export const seme = (testo) => bytesToHex(sha256(utf8("lattice-caveau-v1:" + normalizza(testo))));

/// Parole che non stanno nell'elenco BIP-39: quasi sempre un errore di battitura.
export function fuoriElenco(testo) {
  const set = new Set(PAROLE);
  return normalizza(testo).split(" ").filter((p) => p && !set.has(p));
}

/// Le chiavi di questa identità, ricavate dalle 12 frasi. Deterministiche.
export function chiaviDaSeme(frasi, lns) {
  const indirizzo = String(lns || "").trim().toLowerCase();
  if (!indirizzo) throw new Error("Serve l'indirizzo LNS: la chiave nasce dalle parole E dall'indirizzo.");
  const parole = normalizza(frasi).split(" ").filter(Boolean);
  if (parole.length !== 12) throw new Error("Servono esattamente 12 parole.");
  const semeBytes = argon2id(utf8(seme(frasi)), utf8("lattice-seme-chiave-v1:" + indirizzo), ARGON);
  const kp = ml_dsa65.keygen(semeBytes);
  return { publicKeyHex: bytesToHex(kp.publicKey), secretKeyHex: bytesToHex(kp.secretKey) };
}

/// Il file chiave che l'identità avrebbe se l'avessimo su disco: vive solo in memoria.
export function identitaDaSeme(frasi, lns) {
  const k = chiaviDaSeme(frasi, lns);
  return {
    version: 1,
    algorithm: "ML-DSA-65",
    lns_name: String(lns).trim().toLowerCase(),
    public_key_hex: k.publicKeyHex,
    secret_key_hex: k.secretKeyHex,
    seme: true,
  };
}

/// DODICI PAROLE DA 16 BYTE DI CASO PURO (BIP-39: 128 bit + 4 di controllo).
/// Stesso schema e stesso elenco dell'app: le parole nate qui valgono anche là.
export function frasiNuove() {
  const ent = new Uint8Array(16);
  crypto.getRandomValues(ent);
  const cs = sha256(ent)[0] >> 4;
  let bits = "";
  for (const b of ent) bits += b.toString(2).padStart(8, "0");
  bits += cs.toString(2).padStart(4, "0");
  const out = [];
  for (let i = 0; i < 12; i++) out.push(PAROLE[parseInt(bits.slice(i * 11, i * 11 + 11), 2) % 2048]);
  return out;
}
